/**
 * Tenant branding, applied as CSS custom properties.
 *
 * Writing the palette onto `:root` is what makes white-labelling a configuration change
 * rather than a build: Tailwind's theme reads every colour through these variables, so
 * one write repaints the entire interface.
 */

import { create } from 'zustand';

import { api } from '@/lib/api-client';
import { hexToHslChannels } from '@/lib/utils';

export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * A single-sign-on provider the organisation has turned on.
 *
 * Optional on the payload, and the list drives the sign-in page directly: an
 * organisation with no providers configured gets no social section at all rather
 * than buttons that lead nowhere. `start_url` is the backend's authorisation entry
 * point and is followed as a full navigation, because the flow leaves the app.
 */
export interface SsoProvider {
  /** Stable identifier — `google`, `microsoft`, `saml`. Selects the brand mark. */
  id: string;
  /** The label on the button, as the organisation wants it worded. */
  name: string;
  start_url: string;
}

export interface Brand {
  organisation_name: string;
  product_name: string;
  tagline: string;
  logo_url: string | null;
  favicon_url: string | null;
  colors: Record<string, string>;
  theme_mode: ThemeMode;
  font_family: string;
  login_message: string;
  /** Where an unauthenticated user is sent for help — a service desk, an inbox. */
  support_email: string;
  support_url: string;
  /** Absent until the backend exposes SSO; treated as "none configured". */
  sso_providers?: SsoProvider[];
}

/**
 * Maps a backend colour token to the CSS variable Tailwind reads.
 *
 * Split into two groups because they behave differently across themes.
 *
 * BRAND tokens are the organisation's identity — they are applied in every theme. A
 * tenant that chose purple wants purple whether the reader is in light or dark mode.
 */
const BRAND_TOKENS: Record<string, string> = {
  primary: '--primary',
  primary_foreground: '--primary-foreground',
  accent: '--accent',
  destructive: '--destructive',
  success: '--success',
  warning: '--warning',
};

/**
 * NEUTRAL tokens are the surface ramp, and they are applied in light mode only.
 *
 * The stored values for these are light-mode colours — `background` is the off-white
 * page, `foreground` the near-black slate — and they are written as inline styles on `:root`, which beat
 * any stylesheet rule including `[data-theme='dark']`. Applying them in dark mode
 * therefore produced a white page whose cards, inputs and borders were still dark,
 * because those variables are not in this map and kept their dark-theme values.
 *
 * The ramp is not really a branding decision anyway: a tenant picks its brand hues, and
 * the product owns the neutral scale that keeps text legible against them.
 */
const NEUTRAL_TOKENS: Record<string, string> = {
  background: '--background',
  foreground: '--foreground',
  muted: '--muted',
  secondary: '--secondary',
};

interface BrandState {
  brand: Brand | null;
  isLoading: boolean;
  themeMode: ThemeMode;

  load: () => Promise<void>;
  setThemeMode: (mode: ThemeMode) => void;
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyBrand(brand: Brand, modeOverride?: ThemeMode): void {
  const root = document.documentElement;
  const theme = resolveTheme(modeOverride ?? brand.theme_mode);

  // Set before the variables so anything reading computed styles during this tick
  // sees the theme and its palette agree.
  root.setAttribute('data-theme', theme);

  const write = (variable: string, value: string) => {
    const channels = hexToHslChannels(value);
    // A malformed token is skipped rather than written; the default stays in force.
    if (channels) root.style.setProperty(variable, channels);
  };

  for (const [token, variable] of Object.entries(BRAND_TOKENS)) {
    const value = brand.colors[token];
    if (value) write(variable, value);
  }

  for (const [token, variable] of Object.entries(NEUTRAL_TOKENS)) {
    if (theme === 'light') {
      const value = brand.colors[token];
      if (value) write(variable, value);
    } else {
      // Removing rather than overwriting hands the variable back to the stylesheet,
      // so the dark theme's own ramp applies — and so a light→dark→light round trip
      // restores exactly what the tenant configured.
      root.style.removeProperty(variable);
    }
  }

  // The focus ring follows the brand rather than staying on the default blue; an
  // organisation's focus indicator looking unrelated to its palette reads as a bug.
  if (brand.colors.primary) write('--ring', brand.colors.primary);

  if (brand.font_family) {
    root.style.setProperty('--font-sans', brand.font_family);
  }

  if (brand.product_name) {
    document.title = brand.product_name;
  }

  if (brand.favicon_url) {
    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.appendChild(icon);
    }
    icon.href = brand.favicon_url;
  }
}

export const useBrandStore = create<BrandState>((set, get) => ({
  brand: null,
  isLoading: false,
  // Light until the tenant's brand says otherwise. This is the value in force during
  // the first paint, before `/branding/public/` resolves — defaulting it to 'system'
  // meant a reader on a dark-mode device saw the shell render dark and then swap, and
  // the product's identity is the light palette, not the device's preference.
  themeMode: 'light',

  load: async () => {
    set({ isLoading: true });
    try {
      const brand = await api.get<Brand>('/branding/public/', { anonymous: true });
      applyBrand(brand);
      set({ brand, themeMode: brand.theme_mode, isLoading: false });
    } catch {
      // Branding is presentation. If it cannot be fetched — the tenant is unresolved,
      // the network is down — the application still renders with the default palette
      // rather than blocking sign-in behind a cosmetic request.
      set({ isLoading: false });
    }
  },

  setThemeMode: (mode) => {
    const { brand } = get();

    if (brand) {
      // Re-applied rather than just re-labelled: which palette entries are written
      // depends on the resolved theme, so switching mode has to rewrite them.
      applyBrand(brand, mode);
      set({ brand: { ...brand, theme_mode: mode } });
    } else {
      document.documentElement.setAttribute('data-theme', resolveTheme(mode));
    }

    set({ themeMode: mode });
  },
}));

/*
 * Follow the device while the preference is "system".
 *
 * Without this, a reader whose machine switches to dark at sunset keeps the light
 * palette until they reload. The listener is registered once at module scope and
 * re-reads the store, so it costs nothing per render.
 */
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { brand, themeMode } = useBrandStore.getState();
    if (themeMode !== 'system') return;

    if (brand) applyBrand(brand, 'system');
    else document.documentElement.setAttribute('data-theme', resolveTheme('system'));
  });
}
